import { useCallback, useRef, useState } from 'react'

import chunk from 'lodash/chunk'

import { Currency, fetchQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import type { PaginatedResult } from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'

import { apiQueryClient } from 'features/api/apiQueryClient'
import { InvoiceListItem } from 'Invoices/types'

// Small enough that a page's balance reads finish in a few batches.
const PAGE_SIZE = 20
// Balance reads go out in small batches rather than one burst per page.
const BALANCE_READ_BATCH_SIZE = 6

type Params = {
  customerId: string
  /** Only invoices in this currency are selectable, so it filters server-side. */
  invoiceCurrency: Currency
  /**
   * Which listed invoices are worth a single-read for their balance. Must be
   * stable across renders. Omit to resolve none.
   */
  shouldResolveBalance?: (invoice: InvoiceListItem) => boolean
  /** Comma-separated payment statuses to leave out of the results. */
  excludePaymentStatuses?: string
}

/**
 * Paginated search over a customer's SENT invoices, for the async select
 * controls. Uses getInvoices (not the list summary) because that is the
 * richest list projection, though neither carries `balance`.
 */
export const useInvoicePaginatedFetcher = ({
  customerId,
  invoiceCurrency,
  shouldResolveBalance,
  excludePaymentStatuses
}: Params) => {
  // Every invoice returned so far, so a selected id resolves without a refetch.
  const seenInvoices = useRef(new Map<string, InvoiceListItem>())
  // Balances backfilled from single-reads, keyed by invoice id.
  const [resolvedBalances, setResolvedBalances] = useState<
    Record<string, string>
  >({})
  // Invoices with a balance read in flight, so callers can hold them back
  // until the amount is known rather than showing a value that may change.
  const [pendingBalanceIds, setPendingBalanceIds] = useState<string[]>([])
  const requestedBalanceIds = useRef(new Set<string>())

  /**
   * Backfills balances for a page of listed invoices. Neither list endpoint
   * returns `balance`, so the only source is the single-read — hence one read
   * per invoice, batched, and only for the ones the caller cares about.
   */
  const resolveBalances = useCallback(
    async (invoices: InvoiceListItem[]) => {
      const pending = invoices.filter(
        invoice =>
          invoice.balance === undefined &&
          !requestedBalanceIds.current.has(invoice.id) &&
          Boolean(shouldResolveBalance?.(invoice))
      )

      if (pending.length === 0) return

      pending.forEach(invoice => requestedBalanceIds.current.add(invoice.id))
      setPendingBalanceIds(previous => [
        ...previous,
        ...pending.map(invoice => invoice.id)
      ])

      for (const batch of chunk(pending, BALANCE_READ_BATCH_SIZE)) {
        const resolved = await Promise.all(
          batch.map(async invoice => {
            try {
              const full = await fetchQuery(
                apiQueryClient,
                dashboardv99990101Client.getInvoice,
                { id: invoice.id },
                { staleTime: 1 * MINUTES }
              )

              return full?.balance === undefined
                ? undefined
                : ([invoice.id, full.balance] as const)
            } catch {
              // A failed read leaves the amount unknown; allow a later retry.
              requestedBalanceIds.current.delete(invoice.id)
              return undefined
            }
          })
        )

        const entries = resolved.flatMap(entry => (entry ? [entry] : []))
        if (entries.length > 0) {
          setResolvedBalances(previous => ({
            ...previous,
            ...Object.fromEntries(entries)
          }))
        }

        // Same tick as the balances above, so an invoice stops being pending
        // and gains its amount in one render.
        const batchIds = batch.map(invoice => invoice.id)
        setPendingBalanceIds(previous =>
          previous.filter(id => !batchIds.includes(id))
        )
      }
    },
    [shouldResolveBalance]
  )

  /**
   * Whether an invoice's balance is still on its way. False once the read has
   * settled, including when it produced no balance — waiting longer won't help,
   * so the caller can show the invoice with no amount rather than hide it.
   */
  const isBalancePending = useCallback(
    (invoiceId: string) => pendingBalanceIds.includes(invoiceId),
    [pendingBalanceIds]
  )

  const paginatedFetcher = useCallback(
    async (
      searchTerm: string,
      cursor?: string
    ): Promise<PaginatedResult<InvoiceListItem>> => {
      const data = await fetchQuery(
        apiQueryClient,
        dashboardv99990101Client.getInvoices,
        {
          customerId,
          invoiceCurrency,
          invoiceStatus: 'SENT' as const,
          excludeInvoicePaymentStatus: excludePaymentStatuses,
          search: searchTerm || undefined,
          limit: PAGE_SIZE,
          after: cursor
        },
        { staleTime: 1 * MINUTES }
      )

      const items = data?.items ?? []
      const pagination = data?.pagination

      seenInvoices.current = new Map([
        ...seenInvoices.current,
        ...items.map(invoice => [invoice.id, invoice] as const)
      ])

      // Not awaited: the options render immediately and the amounts fill in.
      void resolveBalances(items)

      return {
        items,
        cursor: pagination?.after,
        hasMore: items.length === PAGE_SIZE && Boolean(pagination?.after)
      }
    },
    [customerId, invoiceCurrency, excludePaymentStatuses, resolveBalances]
  )

  const resolveInvoice = useCallback(
    async (invoiceId: string): Promise<InvoiceListItem | undefined> => {
      const seen = seenInvoices.current.get(invoiceId)
      if (seen?.balance !== undefined) return seen

      // Only the single-read carries balance; staleTime 0 keeps it fresh for OCC.
      const invoice = await fetchQuery(
        apiQueryClient,
        dashboardv99990101Client.getInvoice,
        { id: invoiceId },
        { staleTime: 0 }
      )

      return invoice ?? undefined
    },
    []
  )

  return {
    paginatedFetcher,
    resolveInvoice,
    resolvedBalances,
    isBalancePending,
    isResolvingBalances: pendingBalanceIds.length > 0
  }
}
